import React, { useEffect, useState } from 'react';
import useLicenseStore from '../../store/licenseStore';
import { formatMoney } from '../../utils/format';
import generateYearDebriefPDF from '../../utils/generateYearDebriefPDF';
import {
  Trophy, XCircle, Zap, Warehouse, Truck, UserPlus, Flag,
  TrendingUp, Users, Star, Timer, BarChart3, Download,
} from 'lucide-react';

/**
 * AnnualYearDebrief — 25/07/2026 (demande user prod).
 * Onglet « Débrief de l'année » du Conseil d'administration : frise
 * chronologique des points de passage (AO, événements, dépôts, flotte,
 * recrutements) mois par mois + comparaison inter-joueurs (« meilleur que
 * X % des joueurs ») calculée par le backend sur la cohorte de la même
 * année de jeu (télémétrie /telemetry/year-debrief).
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const CHECKPOINT_STYLE = {
  ao_won: { icon: Trophy, color: 'text-emerald-300' },
  ao_lost: { icon: XCircle, color: 'text-slate-500' },
  event: { icon: Zap, color: 'text-amber-300' },
  depot: { icon: Warehouse, color: 'text-sky-300' },
  fleet: { icon: Truck, color: 'text-indigo-300' },
  hr: { icon: UserPlus, color: 'text-purple-300' },
};

const PCT_LABELS = [
  { key: 'treasury', label: 'Trésorerie' },
  { key: 'year_profit', label: 'Résultat net' },
  { key: 'year_revenue', label: "Chiffre d'affaires" },
  { key: 'fleet_total', label: 'Taille de flotte' },
  { key: 'reputation', label: 'Réputation' },
];

const pctTone = (p) => p >= 70 ? 'text-emerald-300' : p >= 40 ? 'text-amber-300' : 'text-slate-400';

export function PercentileBadges({ percentiles, sampleSize, testIdPrefix = 'debrief' }) {
  if (!percentiles) return null;
  if ((sampleSize || 0) < 4) {
    return (
      <p className="text-[10px] text-slate-500 italic" data-testid={`${testIdPrefix}-percentiles-pending`}>
        Comparaison inter-joueurs disponible dès que d'autres joueurs auront clôturé cette année
        ({sampleSize || 1} clôture{(sampleSize || 1) > 1 ? 's' : ''} enregistrée{(sampleSize || 1) > 1 ? 's' : ''}).
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-percentiles`}>
      {PCT_LABELS.map(({ key, label }) => (percentiles[key] === null || percentiles[key] === undefined) ? null : (
        <span key={key} className="px-2 py-0.5 rounded-full bg-ink-600/60 border border-ink-500/40 text-[10px]">
          <span className="text-slate-400">{label} : </span>
          <span className={`font-bold ${pctTone(percentiles[key])}`}>meilleur que {percentiles[key]} %</span>
          <span className="text-slate-500"> des joueurs</span>
        </span>
      ))}
      {(percentiles.pace !== null && percentiles.pace !== undefined) && (
        <span className="px-2 py-0.5 rounded-full bg-ink-600/60 border border-ink-500/40 text-[10px]">
          <Timer className="w-2.5 h-2.5 inline mr-0.5 text-sky-300" />
          <span className={`font-bold ${pctTone(percentiles.pace)}`}>plus rapide que {percentiles.pace} %</span>
          <span className="text-slate-500"> des joueurs (temps réel)</span>
        </span>
      )}
    </div>
  );
}

export default function AnnualYearDebrief({ debrief, companyName }) {
  const licenseKey = useLicenseStore(s => s.licenseKey);
  const holderName = useLicenseStore(s => s.holderName);
  const [pct, setPct] = useState(null);
  const [sampleSize, setSampleSize] = useState(0);

  // 27/07/2026 (demande user) — export PDF à remettre au responsable
  // pédagogique / portfolio de compétences.
  const exportPdf = () => {
    try {
      generateYearDebriefPDF({
        debrief, companyName, holderName,
        percentiles: pct, sampleSize,
      });
    } catch (err) {
      console.warn('[debrief] PDF export failed:', err);
    }
  };

  useEffect(() => {
    if (!debrief || !licenseKey) return;
    const k = debrief.kpis || {};
    const run = async () => {
      try {
        await fetch(`${BACKEND}/api/telemetry/year-debrief`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            license_key: licenseKey,
            year: debrief.year,
            total_days: k.totalDaysEnd || 0,
            treasury: k.treasuryEnd || 0,
            year_revenue: k.totalRevenue || 0,
            year_profit: k.netResult || 0,
            fleet_total: k.fleetEnd || 0,
            drivers_total: k.driversEnd || 0,
            reputation: k.reputation || 0,
            notoriety: k.notoriety || 0,
            phase: k.phase || 1,
            play_time_seconds: k.playTimeSeconds || 0,
            ao_won: k.aoWon || 0,
            contracts_completed: k.contractsCompleted || 0,
            company_name: companyName || '',
          }),
        });
        const resp = await fetch(
          `${BACKEND}/api/telemetry/year-debrief/percentiles?license_key=${encodeURIComponent(licenseKey)}&year=${debrief.year}`,
        );
        if (resp.ok) {
          const data = await resp.json();
          setSampleSize(data.sample_size || 0);
          if (data.ok) setPct(data.percentiles || null);
        }
      } catch (err) {
        console.warn('[debrief] telemetry failed:', err);
      }
    };
    run();
  }, [debrief, licenseKey, companyName]);

  if (!debrief) return null;
  const k = debrief.kpis || {};
  const byMonth = {};
  (debrief.checkpoints || []).forEach(c => {
    (byMonth[c.monthIndex] = byMonth[c.monthIndex] || []).push(c);
  });

  return (
    <div className="space-y-4" data-testid="annual-debrief-panel">
      {/* Export PDF */}
      <div className="flex justify-end">
        <button
          onClick={exportPdf}
          data-testid="debrief-export-pdf-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-600/30 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Exporter en PDF
        </button>
      </div>
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-2">
        <DebriefKpi icon={TrendingUp} label="CA de l'année" value={formatMoney(k.totalRevenue)} tone={k.totalRevenue >= 0 ? 'text-money' : 'text-danger'} />
        <DebriefKpi icon={BarChart3} label="Résultat net" value={`${k.netResult >= 0 ? '+' : ''}${formatMoney(k.netResult)}`} tone={k.netResult >= 0 ? 'text-money' : 'text-danger'} />
        <DebriefKpi icon={Flag} label="AO remportés" value={k.aoWon} tone="text-emerald-300" />
        <DebriefKpi icon={Truck} label="Flotte fin d'année" value={k.fleetEnd} tone="text-slate-200" />
        <DebriefKpi icon={Users} label="Effectif chauffeurs" value={k.driversEnd} tone="text-slate-200" />
        <DebriefKpi icon={Star} label="Réputation" value={`${k.reputation} / 100`} tone="text-warn" />
      </div>

      {/* Comparaison inter-joueurs */}
      <div className="bg-ink-700/30 rounded-xl p-3 border border-white/5 space-y-1.5">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-mono font-bold">
          Vous vs les autres joueurs — année {debrief.year}
        </p>
        {pct === null && (sampleSize || 0) === 0
          ? <p className="text-[10px] text-slate-500 italic">Comparaison en cours de chargement…</p>
          : <PercentileBadges percentiles={pct || {}} sampleSize={sampleSize} />}
      </div>

      {/* Frise chronologique */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-mono font-bold">
          Points de passage de l'année
        </p>
        {(debrief.months || []).map((m, idx) => {
          const cps = byMonth[idx + 1] || [];
          const pos = (m.netResult || 0) >= 0;
          return (
            <div key={`m-${m.month}-${idx}`} className="flex gap-3" data-testid={`debrief-month-${m.month}`}>
              {/* Rail */}
              <div className="flex flex-col items-center">
                <div className={`w-2.5 h-2.5 rounded-full mt-1 ${pos ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                {idx < (debrief.months.length - 1) && <div className="w-px flex-1 bg-ink-600" />}
              </div>
              <div className="flex-1 pb-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-200">{MONTHS_FR[m.month] || `Mois ${m.month}`}</p>
                  <p className={`text-[10px] font-mono font-bold ${pos ? 'text-money' : 'text-danger'}`}>
                    {pos ? '+' : ''}{formatMoney(m.netResult)}
                  </p>
                </div>
                {cps.length === 0
                  ? <p className="text-[10px] text-slate-600 italic">Exploitation courante, rien à signaler.</p>
                  : (
                    <ul className="mt-0.5 space-y-0.5">
                      {cps.map((c, i) => {
                        const st = CHECKPOINT_STYLE[c.type] || CHECKPOINT_STYLE.event;
                        const Icon = st.icon;
                        return (
                          <li key={`${c.type}-${c.day}-${i}`} className="flex items-center gap-1.5 text-[11px] text-slate-300" data-testid="debrief-checkpoint-row">
                            <Icon className={`w-3 h-3 shrink-0 ${st.color}`} />
                            <span className="truncate">{c.label}</span>
                            {(c.impact !== undefined && c.impact !== 0) && (
                              <span className={`font-mono text-[10px] shrink-0 ${c.impact > 0 ? 'text-money' : 'text-danger'}`}>
                                {c.impact > 0 ? '+' : ''}{formatMoney(c.impact)}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[9px] text-slate-600 italic">
        Ce débrief est aussi transmis à votre responsable pédagogique (vue supervision).
      </p>
    </div>
  );
}

function DebriefKpi({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-ink-700/30 rounded-lg p-2.5 text-center border border-ink-600">
      <Icon className={`w-4 h-4 mx-auto mb-0.5 ${tone}`} />
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-bold font-mono ${tone}`}>{value}</p>
    </div>
  );
}
